import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { api, resolveImage } from "@/lib/api";
import { Btn, labelCls } from "../../admin/AdminUI";
import { Camera, Upload, Image as ImageIcon, Trash2, Loader2, X, Link2, Search } from "lucide-react";

// Universal single-image picker sitting on top of the canonical Media Library.
// value: a media id, legacy /gallery path, or external URL. onChange(ref:string).
export default function MediaPicker({
  label,
  value = "",
  onChange,
  allowCapture = false,
  allowExternalUrl = false,
  previewFit = "cover", // "cover" | "contain"
  previewClass = "h-40",
  hint,
  testid = "media",
}) {
  const camRef = useRef();
  const upRef = useRef();
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showLib, setShowLib] = useState(false);
  const [lib, setLib] = useState([]);
  const [q, setQ] = useState("");
  const [showUrl, setShowUrl] = useState(false);

  const uploadFile = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    setProgress(0);
    try {
      const r = await api.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || e.loaded || 1)) * 100)),
      });
      onChange(r.data.id);
      toast.success("Uploaded — click Save to publish");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setBusy(false);
      setProgress(0);
      if (camRef.current) camRef.current.value = "";
      if (upRef.current) upRef.current.value = "";
    }
  };

  const openLib = async () => {
    setShowLib(true);
    try {
      const r = await api.get("/admin/media");
      setLib(r.data.filter((m) => !m.is_video));
    } catch {
      setLib([]);
    }
  };
  const pick = (id) => {
    onChange(id);
    setShowLib(false);
  };

  const filtered = q
    ? lib.filter((m) => (m.original_filename || "").toLowerCase().includes(q.toLowerCase()))
    : lib;

  const hasValue = !!value;
  const fitCls = previewFit === "contain" ? "object-contain" : "object-cover";

  return (
    <div data-testid={`media-picker-${testid}`}>
      {label && <label className={labelCls}>{label}</label>}

      <div
        className={`relative ${previewClass} rounded-xl overflow-hidden border border-gray-200 bg-gray-50 flex items-center justify-center`}
        data-testid={`media-preview-${testid}`}
      >
        {hasValue ? (
          <img src={resolveImage(value)} alt="" className={`absolute inset-0 w-full h-full ${fitCls}`} />
        ) : (
          <span className="text-xs text-gray-400 flex flex-col items-center gap-1">
            <ImageIcon className="w-6 h-6 opacity-40" />
            No image selected
          </span>
        )}
        {busy && (
          <span className="absolute bottom-1 right-1 bg-black/70 text-white text-[10px] px-2 py-0.5 rounded-full flex items-center gap-1">
            <Loader2 className="w-3 h-3 animate-spin" /> {progress}%
          </span>
        )}
      </div>

      <input ref={upRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid={`up-${testid}`} />
      {allowCapture && (
        <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid={`cam-${testid}`} />
      )}

      <div className="flex flex-wrap gap-2 mt-2">
        <Btn variant="ghost" onClick={() => upRef.current?.click()} disabled={busy} data-testid={`upload-${testid}`}>
          <Upload className="w-4 h-4" /> Upload Image
        </Btn>
        {allowCapture && (
          <Btn variant="ghost" onClick={() => camRef.current?.click()} disabled={busy} data-testid={`take-${testid}`}>
            <Camera className="w-4 h-4" /> Take Photo
          </Btn>
        )}
        <Btn variant="ghost" onClick={openLib} disabled={busy} data-testid={`library-${testid}`}>
          <ImageIcon className="w-4 h-4" /> Choose From Media Library
        </Btn>
        {hasValue && (
          <Btn variant="danger" onClick={() => onChange("")} disabled={busy} data-testid={`remove-${testid}`}>
            <Trash2 className="w-4 h-4" /> Remove
          </Btn>
        )}
      </div>

      {allowExternalUrl && (
        <div className="mt-2">
          <button type="button" onClick={() => setShowUrl((s) => !s)} className="text-xs text-gray-500 hover:text-[var(--action)] inline-flex items-center gap-1" data-testid={`adv-${testid}`}>
            <Link2 className="w-3 h-3" /> Advanced: use external image URL
          </button>
          {showUrl && (
            <input
              className="mt-1 w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 ring-[var(--action)]"
              placeholder="https://example.com/image.jpg or /gallery/soft-wash.jpg"
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              data-testid={`url-${testid}`}
            />
          )}
        </div>
      )}

      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}

      {showLib && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowLib(false)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8" onClick={(e) => e.stopPropagation()} data-testid="media-library-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="font-bold text-[var(--navy)] uppercase">Choose From Media Library</h3>
              <button onClick={() => setShowLib(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="px-5 py-3 border-b">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  className="w-full text-sm pl-9 pr-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 ring-[var(--action)]"
                  placeholder="Search media…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  data-testid="media-library-search"
                />
              </div>
            </div>
            <div className="p-4">
              {filtered.length === 0 ? (
                <p className="text-sm text-gray-400 p-6 text-center">No images found. Upload one from the field.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-[60vh] overflow-y-auto">
                  {filtered.map((m) => (
                    <button
                      key={m.id}
                      onClick={() => pick(m.id)}
                      className="border border-gray-200 rounded-lg overflow-hidden hover:ring-2 ring-[var(--action)] group"
                      data-testid={`lib-pick-${m.id}`}
                      title={m.original_filename}
                    >
                      <img src={resolveImage(m.id)} alt={m.original_filename} className="h-24 w-full object-cover" />
                      <span className="block text-[10px] text-gray-500 truncate px-1 py-0.5">{m.original_filename}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
